// Lossless XML tree for CoLOS .itf files — hand-written, linear scan, no DOM.
//
// Node shapes (plain objects):
//   { type:'document', children }
//   { type:'element', name, attrs:[{name, value, quote}], children, selfClosing }
//   { type:'text', value }        (decoded)
//   { type:'comment', value }
//   { type:'pi', value }          (processing instruction / XML declaration, kept verbatim)
//
// Losslessness: every parsed node remembers its exact source text in
// non-enumerable properties (_raw / _startRaw / _endRaw) together with a
// snapshot of the values it was parsed from. The serializer re-emits the
// source text only while the node still matches that snapshot; anything
// modified or newly built is written canonically (CoLOS style: `<Name />`).

export class XmlError extends Error {
  constructor(reason, offset) {
    super(`${reason} (at offset ${offset})`);
    this.name = 'XmlError';
    this.reason = reason;
    this.offset = offset;
  }
}

function hide(obj, key, value) {
  Object.defineProperty(obj, key, { value, writable: true, configurable: true, enumerable: false });
}

const HIDDEN_KEYS = ['_raw', '_rawValue', '_startRaw', '_startSig', '_rawSelfClosing', '_endRaw', '_endName', '_offset'];

// ---------------------------------------------------------------- characters

// XML 1.0 Char production: #x9 | #xA | #xD | [#x20-#xD7FF] | [#xE000-#xFFFD] | [#x10000-#x10FFFF].
// With the `u` flag an unpaired surrogate is a code point outside every range, so it matches too.
const ILLEGAL_CHAR = /[^\t\n\r\x20-퟿-�\u{10000}-\u{10FFFF}]/u;

/** True if the code point is allowed in an XML 1.0 document. */
export function isXmlChar(cp) {
  return cp === 0x9 || cp === 0xa || cp === 0xd || (cp >= 0x20 && cp <= 0xd7ff) ||
    (cp >= 0xe000 && cp <= 0xfffd) || (cp >= 0x10000 && cp <= 0x10ffff);
}

/** UTF-16 index of the first character not allowed in XML 1.0 (incl. lone surrogates), or -1. */
export function findIllegalChar(s) {
  const m = ILLEGAL_CHAR.exec(s);
  return m ? m.index : -1;
}

/** 'U+000B' for the (code point at the) given index. */
export function describeChar(s, i) {
  return 'U+' + s.codePointAt(i).toString(16).toUpperCase().padStart(4, '0');
}

// ---------------------------------------------------------------- entities

const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

function decodeEntities(raw, base) {
  let amp = raw.indexOf('&');
  if (amp === -1) return raw;
  let out = '';
  let pos = 0;
  while (amp !== -1) {
    out += raw.slice(pos, amp);
    const semi = raw.indexOf(';', amp + 1);
    if (semi === -1 || semi - amp > 12) throw new XmlError('Bad entity: "&" must be written as &amp;', base + amp);
    const name = raw.slice(amp + 1, semi);
    let ch;
    if (Object.prototype.hasOwnProperty.call(NAMED, name)) {
      ch = NAMED[name];
    } else if (name.charCodeAt(0) === 35 /* # */) {
      const hex = name.charCodeAt(1) === 120 /* x */ || name.charCodeAt(1) === 88;
      const digits = name.slice(hex ? 2 : 1);
      const ok = digits.length > 0 && (hex ? /^[0-9a-fA-F]+$/ : /^[0-9]+$/).test(digits);
      const cp = ok ? parseInt(digits, hex ? 16 : 10) : NaN;
      if (!ok || !isXmlChar(cp)) {
        throw new XmlError(`Bad character reference &${name};`, base + amp);
      }
      ch = String.fromCodePoint(cp);
    } else {
      throw new XmlError(`Unknown entity &${name};`, base + amp);
    }
    out += ch;
    pos = semi + 1;
    amp = raw.indexOf('&', pos);
  }
  return out + raw.slice(pos);
}

// CR is written as a character reference: a conforming parser would otherwise normalise a literal CR away.
function escapeText(s) {
  return String(s).replace(/[&<>\r]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&#13;'));
}

function escapeAttr(s, quote) {
  return String(s).replace(/[&<"'\t\n\r]/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '"': return quote === '"' ? '&quot;' : c;
      case "'": return quote === "'" ? '&apos;' : c;
      case '\t': return '&#9;';
      case '\n': return '&#10;';
      default: return '&#13;';
    }
  });
}

// ---------------------------------------------------------------- parser

function isWs(c) {
  return c === 32 || c === 9 || c === 10 || c === 13;
}

// Name ends at whitespace or one of  / > = < " '
function isNameEnd(c) {
  return isWs(c) || c === 47 || c === 62 || c === 61 || c === 60 || c === 34 || c === 39;
}

function startSig(el) {
  let s = el.name;
  for (const a of el.attrs) s += '\u0000' + a.name + '\u0001' + a.value + '\u0001' + (a.quote === "'" ? "'" : '"');
  return s;
}

/**
 * @param {string} text
 * @returns {{type:'document', children:any[]}}
 */
export function parseXml(text) {
  if (typeof text !== 'string') throw new TypeError('parseXml expects a string');
  const illegal = findIllegalChar(text);
  if (illegal !== -1) throw new XmlError(`Illegal character ${describeChar(text, illegal)}`, illegal);
  const n = text.length;
  const doc = { type: 'document', children: [] };
  const stack = [doc];
  let rootSeen = false;
  let i = 0;

  const parentOf = () => stack[stack.length - 1];

  const readName = (j) => {
    let k = j;
    while (k < n && !isNameEnd(text.charCodeAt(k))) k++;
    return text.slice(j, k);
  };

  const addText = (start, end) => {
    const raw = text.slice(start, end);
    if (stack.length === 1) {
      for (let k = 0; k < raw.length; k++) {
        if (!isWs(raw.charCodeAt(k))) throw new XmlError('Text is not allowed outside the root element', start + k);
      }
    }
    const value = decodeEntities(raw, start);
    const node = { type: 'text', value };
    hide(node, '_raw', raw);
    hide(node, '_rawValue', value);
    parentOf().children.push(node);
  };

  while (i < n) {
    const lt = text.indexOf('<', i);
    const stop = lt === -1 ? n : lt;
    if (stop > i) addText(i, stop);
    if (lt === -1) break;
    i = lt;

    if (text.startsWith('<!--', i)) {
      const close = text.indexOf('-->', i + 4);
      if (close === -1) throw new XmlError('Unclosed comment', i);
      parentOf().children.push({ type: 'comment', value: text.slice(i + 4, close) });
      i = close + 3;
      continue;
    }
    if (text.startsWith('<![CDATA[', i)) {
      if (stack.length === 1) throw new XmlError('CDATA is not allowed outside the root element', i);
      const close = text.indexOf(']]>', i + 9);
      if (close === -1) throw new XmlError('Unclosed CDATA section', i);
      const value = text.slice(i + 9, close);
      const node = { type: 'text', value };
      hide(node, '_raw', text.slice(i, close + 3));
      hide(node, '_rawValue', value);
      parentOf().children.push(node);
      i = close + 3;
      continue;
    }
    if (text.startsWith('<?', i)) {
      const close = text.indexOf('?>', i + 2);
      if (close === -1) throw new XmlError('Unclosed processing instruction', i);
      parentOf().children.push({ type: 'pi', value: text.slice(i + 2, close) });
      i = close + 2;
      continue;
    }
    if (text.startsWith('<!', i)) throw new XmlError('DOCTYPE and markup declarations are not supported', i);

    if (text.charCodeAt(i + 1) === 47 /* / */) {
      // end tag
      const name = readName(i + 2);
      let j = i + 2 + name.length;
      while (j < n && isWs(text.charCodeAt(j))) j++;
      if (j >= n) throw new XmlError(`Unclosed end tag </${name}`, i);
      if (text.charCodeAt(j) !== 62) throw new XmlError(`Unexpected character in end tag </${name}>`, j);
      if (stack.length === 1) throw new XmlError(`Unexpected closing tag </${name}>`, i);
      const top = parentOf();
      if (top.name !== name) throw new XmlError(`Mismatched closing tag: expected </${top.name}> but found </${name}>`, i);
      hide(top, '_endRaw', text.slice(i, j + 1));
      hide(top, '_endName', name);
      stack.pop();
      i = j + 1;
      continue;
    }

    // start tag
    const name = readName(i + 1);
    if (!name) throw new XmlError('Invalid tag name', i);
    if (stack.length === 1) {
      if (rootSeen) throw new XmlError('Only one root element is allowed', i);
      rootSeen = true;
    }
    const attrs = [];
    let j = i + 1 + name.length;
    let selfClosing = false;
    for (;;) {
      const wsStart = j;
      while (j < n && isWs(text.charCodeAt(j))) j++;
      if (j >= n) throw new XmlError(`Unclosed tag <${name}`, i);
      const c = text.charCodeAt(j);
      if (c === 62 /* > */) { j++; break; }
      if (c === 47 /* / */) {
        if (text.charCodeAt(j + 1) !== 62) throw new XmlError('Expected "/>"', j);
        j += 2;
        selfClosing = true;
        break;
      }
      if (j === wsStart) throw new XmlError(`Unexpected character "${text[j]}" in tag <${name}>`, j);
      const aname = readName(j);
      if (!aname) throw new XmlError(`Unexpected character "${text[j]}" in tag <${name}>`, j);
      j += aname.length;
      while (j < n && isWs(text.charCodeAt(j))) j++;
      if (text.charCodeAt(j) !== 61 /* = */) throw new XmlError(`Attribute ${aname} has no value`, j);
      j++;
      while (j < n && isWs(text.charCodeAt(j))) j++;
      const q = text[j];
      if (q !== '"' && q !== "'") throw new XmlError(`Value of attribute ${aname} must be quoted`, j);
      const close = text.indexOf(q, j + 1);
      if (close === -1) throw new XmlError(`Unterminated value of attribute ${aname}`, j);
      const raw = text.slice(j + 1, close);
      const bad = raw.indexOf('<');
      if (bad !== -1) throw new XmlError(`"<" is not allowed in attribute ${aname}`, j + 1 + bad);
      if (attrs.some((a) => a.name === aname)) throw new XmlError(`Duplicate attribute ${aname}`, j);
      attrs.push({ name: aname, value: decodeEntities(raw, j + 1), quote: q });
      j = close + 1;
    }
    const el = { type: 'element', name, attrs, children: [], selfClosing };
    hide(el, '_startRaw', text.slice(i, j));
    hide(el, '_startSig', startSig(el));
    hide(el, '_rawSelfClosing', selfClosing);
    hide(el, '_offset', i);
    parentOf().children.push(el);
    if (!selfClosing) stack.push(el);
    i = j;
  }

  if (stack.length > 1) {
    const top = parentOf();
    throw new XmlError(`Unclosed element <${top.name}>`, top._offset);
  }
  if (!rootSeen) throw new XmlError('No root element', n);
  return doc;
}

// ---------------------------------------------------------------- serializer

// Serializer and cloneNode use an explicit stack (no recursion) so arbitrarily deep documents work.

class EndTag {
  constructor(s) {
    this.s = s;
  }
}

function pushChildren(stack, children) {
  for (let k = children.length - 1; k >= 0; k--) stack.push(children[k]);
}

/**
 * @param {object} root document or element
 * @returns {string}
 * @throws {XmlError} if the output would contain a character not allowed in XML 1.0
 */
export function serializeXml(root) {
  const out = [];
  const stack = [root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (node instanceof EndTag) {
      out.push(node.s);
      continue;
    }
    switch (node && node.type) {
      case 'document':
        pushChildren(stack, node.children);
        break;
      case 'text':
        out.push(node._raw !== undefined && node._rawValue === node.value ? node._raw : escapeText(node.value));
        break;
      case 'comment':
        out.push('<!--', node.value, '-->');
        break;
      case 'pi':
        out.push('<?', node.value, '?>');
        break;
      case 'element': {
        const children = node.children || [];
        // Empty elements are written `<Name />` (CoLOS style) — except an empty pair
        // `<Name></Name>` that came from the source and is still flagged non-self-closing.
        const keptPair = node.selfClosing === false && node._rawSelfClosing === false;
        const emitSelfClosing = children.length === 0 && !keptPair;
        if (
          node._startRaw !== undefined &&
          node._rawSelfClosing === emitSelfClosing &&
          node._startSig === startSig(node)
        ) {
          out.push(node._startRaw);
        } else {
          out.push('<', node.name);
          for (const a of node.attrs || []) {
            const q = a.quote === "'" ? "'" : '"';
            out.push(' ', a.name, '=', q, escapeAttr(a.value, q), q);
          }
          out.push(emitSelfClosing ? ' />' : '>');
        }
        if (emitSelfClosing) break;
        stack.push(new EndTag(node._endRaw !== undefined && node._endName === node.name ? node._endRaw : `</${node.name}>`));
        pushChildren(stack, children);
        break;
      }
      default:
        throw new TypeError(`Unknown XML node type: ${node && node.type}`);
    }
  }
  const s = out.join('');
  // Never emit a document an XML 1.0 parser (CoLOS) would reject.
  const bad = findIllegalChar(s);
  if (bad !== -1) throw new XmlError(`Illegal character ${describeChar(s, bad)} cannot be written`, bad);
  return s;
}

// ---------------------------------------------------------------- helpers

/** First element child with this name, or null. */
export function child(el, name) {
  if (!el || !el.children) return null;
  for (const c of el.children) if (c.type === 'element' && c.name === name) return c;
  return null;
}

/** Element children (optionally only those with this name). */
export function childrenOf(el, name) {
  if (!el || !el.children) return [];
  return el.children.filter((c) => c.type === 'element' && (name === undefined || c.name === name));
}

/** Walk a path of child names; null if any step is missing. */
export function at(el, path) {
  let cur = el;
  for (const name of path) {
    cur = child(cur, name);
    if (!cur) return null;
  }
  return cur;
}

/** Concatenated direct text content ('' if el is missing). */
export function textOf(el) {
  if (!el || !el.children) return '';
  let s = '';
  for (const c of el.children) if (c.type === 'text') s += c.value;
  return s;
}

/** Replace children with one text node; '' → no children (serializes `<Name />`). No-op if unchanged. */
export function setText(el, value) {
  const v = value === null || value === undefined ? '' : String(value);
  if (textOf(el) === v && el.children.every((c) => c.type === 'text')) return el;
  el.children = v === '' ? [] : [{ type: 'text', value: v }];
  el.selfClosing = v === '';
  return el;
}

/** Element builder. attrs: object or [{name,value}]; strings/numbers become text nodes. */
export function elem(name, attrs = {}, ...children) {
  const list = Array.isArray(attrs)
    ? attrs.map((a) => ({ name: a.name, value: String(a.value), quote: a.quote === "'" ? "'" : '"' }))
    : Object.entries(attrs || {}).map(([k, v]) => ({ name: k, value: String(v), quote: '"' }));
  const kids = [];
  const add = (c) => {
    if (c === null || c === undefined || c === false) return;
    if (Array.isArray(c)) return c.forEach(add);
    if (typeof c === 'string' || typeof c === 'number' || typeof c === 'boolean') {
      const s = String(c);
      if (s !== '') kids.push({ type: 'text', value: s });
      return;
    }
    kids.push(c);
  };
  children.forEach(add);
  return { type: 'element', name, attrs: list, children: kids, selfClosing: kids.length === 0 };
}

/**
 * Insert `node` before the first existing element sibling that comes later in `order`;
 * append if none (or if node's name is not in `order`). Returns node.
 */
export function insertOrdered(parent, node, order) {
  const idx = order ? order.indexOf(node.name) : -1;
  if (idx !== -1) {
    for (let k = 0; k < parent.children.length; k++) {
      const c = parent.children[k];
      if (c.type !== 'element') continue;
      const ci = order.indexOf(c.name);
      if (ci > idx) {
        parent.children.splice(k, 0, node);
        return node;
      }
    }
  }
  parent.children.push(node);
  return node;
}

/** Existing child, or a new empty one inserted in order. */
export function ensureChild(parent, name, order) {
  return child(parent, name) || insertOrdered(parent, elem(name), order);
}

/** Remove a direct child. Emptied parents serialize as `<Name />`. Returns true if removed. */
export function removeNode(parent, node) {
  const k = parent.children.indexOf(node);
  if (k === -1) return false;
  parent.children.splice(k, 1);
  if (parent.type === 'element' && parent.children.length === 0) parent.selfClosing = true;
  return true;
}

/** Deep copy (keeps source-text snapshots so an unmodified clone serializes identically). */
export function cloneNode(node) {
  const root = shallowCopy(node);
  const stack = [node, root];
  while (stack.length > 0) {
    const dst = stack.pop();
    const src = stack.pop();
    if (src.type !== 'document' && src.type !== 'element') continue;
    for (const c of src.children) {
      const copy = shallowCopy(c);
      dst.children.push(copy);
      if (c.type === 'document' || c.type === 'element') stack.push(c, copy);
    }
  }
  return root;
}

function shallowCopy(node) {
  let copy;
  switch (node.type) {
    case 'document':
      copy = { type: 'document', children: [] };
      break;
    case 'element':
      copy = {
        type: 'element',
        name: node.name,
        attrs: node.attrs.map((a) => ({ ...a })),
        children: [],
        selfClosing: node.selfClosing,
      };
      break;
    default:
      copy = { ...node };
  }
  for (const k of HIDDEN_KEYS) if (node[k] !== undefined) hide(copy, k, node[k]);
  return copy;
}
